import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { MembershipTier } from '@dataTypes/membership.type'
import {
  createMembershipTier,
  getAllMembershipTiers,
  removeMembershipTier,
  updateMembershipTier,
  updateMembershipTierStatus,
} from '@store/actions/membership.action'
import { mapMembershipTierListFromApi } from '@services/api/billing/membership.mapper'

type MembershipState = {
  tiers: MembershipTier[]
  count: number
  loading: boolean
  saving: boolean
  error: string | null
}

const initialState: MembershipState = {
  tiers: [],
  count: 0,
  loading: false,
  saving: false,
  error: null,
}

const membershipSlice = createSlice({
  name: 'membership',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllMembershipTiers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getAllMembershipTiers.fulfilled, (state, action) => {
        state.loading = false
        state.tiers = mapMembershipTierListFromApi(action.payload.tiers)
        state.count = action.payload.count
      })
      .addCase(getAllMembershipTiers.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

      .addCase(createMembershipTier.pending, (state) => {
        state.saving = true
        state.error = null
      })
      .addCase(
        createMembershipTier.fulfilled,
        (state, action: PayloadAction<MembershipTier>) => {
          state.saving = false
          state.tiers.push(action.payload)
          state.count = state.tiers.length
        },
      )
      .addCase(createMembershipTier.rejected, (state, action) => {
        state.saving = false
        state.error = action.payload as string
      })

      .addCase(updateMembershipTier.pending, (state) => {
        state.saving = true
        state.error = null
      })
      .addCase(
        updateMembershipTier.fulfilled,
        (state, action: PayloadAction<MembershipTier>) => {
          state.saving = false
          const index = state.tiers.findIndex(
            (tier) => tier.id === action.payload.id,
          )
          if (index !== -1) {
            state.tiers[index] = action.payload
          }
        },
      )
      .addCase(updateMembershipTier.rejected, (state, action) => {
        state.saving = false
        state.error = action.payload as string
      })

      .addCase(
        updateMembershipTierStatus.fulfilled,
        (state, action: PayloadAction<MembershipTier>) => {
          const index = state.tiers.findIndex(
            (tier) => tier.id === action.payload.id,
          )
          if (index !== -1) {
            state.tiers[index] = action.payload
          }
        },
      )
      .addCase(updateMembershipTierStatus.rejected, (state, action) => {
        state.error = action.payload as string
      })

      .addCase(
        removeMembershipTier.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.tiers = state.tiers.filter((tier) => tier.id !== action.payload)
          state.count = state.tiers.length
        },
      )
      .addCase(removeMembershipTier.rejected, (state, action) => {
        state.error = action.payload as string
      })
  },
})

export default membershipSlice.reducer
